import React, { useEffect } from 'react';
import { LayoutTypes } from '@noths/polaris-client-next-components';
import { addParamsToHref, getPathnameFromUrl } from '@noths/polaris-client-utils';
import type { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { SectionGroup } from 'src/components/dontgiftguide/levelOne/SectionGroup/SectionGroup';
import { SectionGroupHeader } from 'src/components/dontgiftguide/levelOne/SectionGroupHeader/SectionGroupHeader';
import { Accordion } from 'src/components/dontgiftguide/molecules/Accordion/Accordion';
import { Introduction } from 'src/components/dontgiftguide/molecules/Introduction/Introduction';
import { Charity } from '../components/dontgiftguide/molecules/Charity/Charity';
import { Footer } from '../components/dontgiftguide/molecules/Footer/Footer';
import {
  STICKY_HEADER_SPACER_HEIGHT_PX,
  StickyHeader,
} from '../components/dontgiftguide/molecules/StickyHeader/StickyHeader';
import { getDontGiftGuideContent } from '../content/dontgiftguide';
import type { SectionGroupContent } from '../content/dontgiftguide/types';
import { LevelOneContentType } from '../content/dontgiftguide/types';
import type { Environment } from '../types/environment';
import type { BasePageProps } from '../types/pageProps';
import { getSlug } from '../utils/getSlug';
import { generateBaseStyles } from '../styles/dontgiftguide.styles';

const BaseStyles = generateBaseStyles();

export interface DontGiftGuidePageProps extends BasePageProps {
  seo: {
    title: string;
    description: string;
  };
  levelOne: SectionGroupContent[];
  layoutType: LayoutTypes;
}

const createLinkClickHandler = (id: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
  if (e?.currentTarget?.href) {
    e.preventDefault();
    window.history.replaceState(
      null,
      '',
      addParamsToHref(window.location.href, {
        accordionId: id,
        lastLinkClicked: getPathnameFromUrl(e.currentTarget.href),
      }),
    );
    window.location.assign(e.currentTarget.href);
  }
};

const DontGiftGuidePage = ({ clientIsNativeApp, seo, levelOne }: DontGiftGuidePageProps) => {
  const router = useRouter();
  const { accordionId, lastLinkClicked } = router.query;

  const navItems = levelOne
    .filter(({ contentType }) => contentType === LevelOneContentType.SectionGroup)
    .map(({ title }) => {
      return { title, id: getSlug(title) };
    });

  useEffect(() => {
    const links = Array.from(document.links);
    const elementToScrollTo = links.find((link) => link.pathname === lastLinkClicked);
    if (elementToScrollTo) {
      setTimeout(() => {
        window.scrollTo({
          top: elementToScrollTo.offsetTop - STICKY_HEADER_SPACER_HEIGHT_PX,
          behavior: 'smooth',
        });
      }, 0);
    }
  }, [lastLinkClicked]);

  return (
    <>
      <Head>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
      </Head>
      <BaseStyles />
      {!clientIsNativeApp && <StickyHeader navItems={navItems} />}
      <Introduction />
      {levelOne.map((levelOneContent) => {
        switch (levelOneContent.contentType) {
          case LevelOneContentType.SectionGroup:
            const id = getSlug(levelOneContent.title);
            return (
              <Accordion
                key={id}
                title={levelOneContent.title}
                darkTitleColor={levelOneContent.darkTitleColor}
                id={id}
                backgroundColor={levelOneContent.backgroundColor}
                open={accordionId === id}
              >
                <SectionGroup
                  onLinkClick={createLinkClickHandler(id)}
                  clientIsNativeApp={clientIsNativeApp}
                  heading={levelOneContent.heading}
                  copy={levelOneContent.copy}
                  sections={levelOneContent.sections}
                  darkTitleColor={levelOneContent.darkTitleColor}
                  categoryHref={levelOneContent.categoryHref}
                  footer={levelOneContent.footer}
                />
              </Accordion>
            );
          case LevelOneContentType.SectionGroupHeader:
            const key = getSlug(levelOneContent.heading);
            return (
              <SectionGroupHeader
                key={key}
                heading={levelOneContent.heading}
                copy={levelOneContent.copy}
              />
            );
          case LevelOneContentType.Charity:
            const charityKey = getSlug(levelOneContent.heading);
            return (
              <Charity
                key={charityKey}
                title={levelOneContent.title}
                heading={levelOneContent.heading}
                copy={levelOneContent.copy}
                sections={levelOneContent.sections}
              />
            );
          default:
            return null;
        }
      })}
      {!clientIsNativeApp && <Footer env={process.env.NODE_ENV as Environment} />}
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const clientIsNativeApp = context.query?.client === 'app';
  const { seo, levelOne } = await getDontGiftGuideContent();

  return {
    props: {
      clientIsNativeApp,
      seo,
      levelOne,
      layoutType: LayoutTypes.MINIMAL,
    },
  };
};

export default DontGiftGuidePage;
